import { type ActionType, createAction } from 'typesafe-actions';
import { createRoutine } from '../common/utils/new-routines.js';
import type { StructuralChange } from '../schema-form/types.js';
import type { Content, ContentId, ContentResponse, Lock } from './types.js';

export type ContentMeta = {
  isSingleton?: boolean;
  shapeId: string;
  contentId?: string;
  locale: string;
  redirectOnSuccess?: string;
  redirectOnFailure?: string;
  callback?: (values: Content, id: string) => void;
};

export type GetContentMeta = {
  contentId: string;
} & ContentMeta;

export type DeleteContentMeta = {
  contentId: string;
} & ContentMeta;

export type CreateContentMeta = {
  source: string;
} & ContentMeta;

export type ContentUpdatedPayload = {
  contentId: string;
  contentTypeId: string;
  contentVersion: number;
};

export type InvalidateContentPayload = {
  shapeId: string;
  isSingleton: boolean;
  contentId: string;
  contentVersion: number;
};

export const getContentList = createRoutine('content/GET_CONTENT_LIST')<
  ContentMeta,
  undefined,
  undefined,
  undefined,
  string,
  undefined
>();

export const getContent = createRoutine('content/GET_CONTENT')<
  GetContentMeta,
  undefined,
  undefined,
  ContentResponse,
  string,
  undefined
>();

export const getContentRequested = getContent.requested.bind(null, undefined);

export type CreatePayload = Record<string, unknown>;

export const createContent = createRoutine('content/CREATE_CONTENT')<
  CreateContentMeta,
  CreatePayload,
  undefined,
  ContentResponse,
  string,
  undefined
>();

export type UpdatePayload = {
  input: Record<string, unknown>;
  structure?: StructuralChange[];
};

export const updateContent = createRoutine('content/UPDATE_CONTENT')<
  GetContentMeta,
  UpdatePayload,
  undefined,
  ContentResponse,
  string,
  undefined
>();

export const contentUpdated = createAction('content/CONTENT_UPDATED')<ContentUpdatedPayload>();
export type ContentUpdatedAction = ActionType<typeof contentUpdated>;

export const invalidateContent = createAction('content/INVALIDATE_CONTENT')<InvalidateContentPayload>();
export type InvalidateContentAction = ActionType<typeof invalidateContent>;

export const duplicateContent = createRoutine('content/DUPLICATE_CONTENT')<
  GetContentMeta,
  UpdatePayload,
  undefined,
  ContentResponse,
  string,
  undefined
>();

export const deleteContent = createRoutine('content/DELETE_CONTENT')<
  DeleteContentMeta,
  undefined,
  undefined,
  { contentId: string; response: boolean },
  string,
  undefined
>();

export type SavePayload = {
  shapeId: string;
  source?: string;
  isNew: boolean;
  isSingleton: boolean;
  contentId?: string;
  locale: string;
} & UpdatePayload;

export const save = createAction('content/SAVE')<SavePayload>();
export type SaveAction = ActionType<typeof save>;

export const saveAndContinue = createAction('content/SAVE_AND_CONTINUE')<SavePayload>();
export type SaveAndContinueAction = ActionType<typeof saveAndContinue>;

export const saveAndDuplicate = createAction('content/SAVE_AND_DUPLICATE')<SavePayload>();
export type SaveAndDuplicateAction = ActionType<typeof saveAndDuplicate>;

export const saveAndAddAnother = createAction('content/SAVE_AND_ADD_ANOTHER')<SavePayload>();
export type SaveAndAddAnotherAction = ActionType<typeof saveAndAddAnother>;

export type Save = typeof save | typeof saveAndContinue | typeof saveAndDuplicate | typeof saveAndAddAnother;

export const editingSession = createRoutine('content/EDITING_SESSION')<
  undefined,
  GetContentMeta,
  GetContentMeta,
  GetContentMeta,
  string,
  GetContentMeta
>();

export const cancelEditingSession = editingSession.cancelled;

export const lockContent = createRoutine('content/LOCK_CONTENT')<
  ContentId,
  undefined,
  undefined,
  { lock: Lock },
  string,
  undefined
>();

export const unlockContent = createRoutine('content/UNLOCK_CONTENT')<
  ContentId,
  undefined,
  undefined,
  undefined,
  string,
  undefined
>();

export const extendLock = createRoutine('content/EXTEND_LOCK')<
  ContentId,
  undefined,
  undefined,
  undefined,
  string,
  undefined
>();

export type LockedPayload = {
  shapeId: string;
  contentId: string;
  lock: Lock;
};

export const contentLocked = createAction('content/CONTENT_LOCKED')<LockedPayload>();

export type UnlockedPayload = {
  shapeId: string;
  contentId: string;
  userId: string;
};

export const contentUnlocked = createAction('content/CONTENT_UNLOCKED')<UnlockedPayload>();

export const breakLock = createRoutine('content/BREAK_LOCK')<
  ContentId,
  undefined,
  undefined,
  undefined,
  string,
  undefined
>();

export type LockRoutine = typeof lockContent | typeof unlockContent | typeof breakLock;

export type LockBrokenPayload = {
  shapeId: string;
  contentId: string;
  user: {
    id: string;
    email: string;
    fullName: string;
  };
};

export const lockBroken = createAction('content/LOCK_BROKEN')<LockBrokenPayload>();
export type LockBrokenAction = ActionType<typeof lockBroken>;

export const cleanupEditingSessions = createAction('content/CLEANUP_EDITING_SESSIONS')();

export type ContentIndexedPayload = {
  shapeId: string;
};
export const contentIndexed = createAction('content/CONTENT_INDEXED')<ContentIndexedPayload>();
export type ContentIndexedAction = ActionType<typeof contentIndexed>;

export type Actions =
  | ActionType<typeof getContentList>
  | ActionType<typeof getContent>
  | ActionType<typeof createContent>
  | ActionType<typeof updateContent>
  | ActionType<typeof contentUpdated>
  | ActionType<typeof invalidateContent>
  | ActionType<typeof deleteContent>
  | ActionType<typeof save>
  | ActionType<typeof saveAndContinue>
  | ActionType<typeof saveAndDuplicate>
  | ActionType<typeof saveAndAddAnother>
  | ActionType<typeof editingSession>
  | ActionType<typeof lockContent>
  | ActionType<typeof unlockContent>
  | ActionType<typeof extendLock>
  | ActionType<typeof contentLocked>
  | ActionType<typeof contentUnlocked>
  | ActionType<typeof breakLock>
  | ActionType<typeof lockBroken>
  | ActionType<typeof cleanupEditingSessions>;
